/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useRef, useState } from 'react';
import { IconButton } from "@/subframe/components/IconButton";
import { DialogLayout } from "@/subframe/layouts/DialogLayout";
import { TextArea } from "@/subframe/components/TextArea";
import { Tooltip } from "@/subframe/components/Tooltip";
import { Button } from "@/subframe/components/Button";
import SubframeCore from "@subframe/core";
import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from "../../redux/store";
import { handleMediaSend } from "../../redux/reducers/mediaMessageSlice";
import SelectDateAndTimePopup from '../subcomponents/SelectDateAndTimePopup';
import { addNotification } from "../../redux/reducers/notificationSlice";
import { NotificationIcon, NotificationType } from "../../Utils/notificationEnums";
import moment from "moment";


interface FileSelectionMenuProps {
  currentRecipient: any;
}

const FileSelectionMenu: React.FC<FileSelectionMenuProps> = ({ currentRecipient }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { system_user_id, token } = useSelector((state: RootState) => state.auth);
  const isMediaUploading = useSelector((state: RootState) => state.mediaMessage.isLoading);

  const [showPreview, setShowPreview] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<any>(null);
  const [caption, setCaption] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isDateTimePopupOpen, setIsDateTimePopupOpen] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileUpload = async (file: File) => {
    try {
      setIsUploading(true);
      setUploadError(null);

      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch('https://api.insightssystem.com/api:uYdlVwac/upload_file_by_attachment', {
        method: 'POST',
        headers: {
          'Accept': '*/*',
          ...(token && { Authorization: token })
        },
        body: formData
      });

      const result = await response.json();



      if (result.success) {
        setUploadedFile(result);

        setShowPreview(true);
      } else {
        setUploadError(result.error || 'Upload failed');
      }
    } catch (error) {
      setUploadError('Failed to upload file. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      await handleFileUpload(file);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    const file = e.dataTransfer.files[0];
    if (file) {
      await handleFileUpload(file);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleSend = async (scheduledTime?: number) => {
    if (!uploadedFile || !system_user_id || !currentRecipient) return;

    if (!caption.trim()) {
      dispatch(
        addNotification({
          id: Date.now(),
          type: NotificationType.ERROR,
          title: "Caption Required",
          message: "Please add a caption before sending",
          icon: NotificationIcon.ERROR,
        })
      );
      return;
    }

    try {
      await dispatch(handleMediaSend({
        media_link: uploadedFile.file_url,
        type: uploadedFile.file_type,
        caption: caption,
        currentRecipient,
        ...(scheduledTime ? { scheduled_time: scheduledTime } : {})
      })).unwrap();

      // Show success notification based on whether scheduled or not
      if (scheduledTime) {
        const formattedDateTime = moment(scheduledTime).format('MMM D, YYYY hh:mm A');
        dispatch(
          addNotification({
            id: Date.now(),
            type: NotificationType.SUCCESS,
            title: "Media Scheduled",
            message: `Your media will be sent on ${formattedDateTime}`,
            icon: NotificationIcon.SUCCESS,
          })
        );
      } else {
        dispatch(
          addNotification({
            id: Date.now(),
            type: NotificationType.SUCCESS,
            title: "Media Sent",
            message: "Your media has been sent successfully",
            icon: NotificationIcon.SUCCESS,
          })
        );
      }

      // Clear states after successful send
      setShowPreview(false);
      setUploadedFile(null);
      setCaption("");
      setUploadError(null);
    } catch (error: any) {
      // Show error notification
      const errorTitle = scheduledTime ? "Schedule Failed" : "Media Not Sent";
      dispatch(
        addNotification({
          id: Date.now(),
          type: NotificationType.ERROR,
          title: errorTitle,
          message: error.message || "Something went wrong. Please try again.",
          icon: NotificationIcon.ERROR,
        })
      );
    }
  };

  const handleScheduleMessage = (scheduledTime: number) => {
    // We'll use the caption from state instead of the text parameter
    handleSend(scheduledTime);
  };

  const renderPreview = () => {
    if (!uploadedFile) return null;

    // const mimeType = uploadedFile.mime_type || '';
    const fileType = uploadedFile.file_type || '';

    if (fileType === "image") {
      return (
        <img
          className="max-h-96 w-auto flex-none rounded-md shadow-lg"
          src={uploadedFile.file_url}
          alt={uploadedFile.original_name}
        />
      );
    }

    if (fileType === "video") {
      return (
        <video
          className="max-h-96 w-auto flex-none rounded-md shadow-lg"
          controls
        >
          <source src={uploadedFile.file_url} type={uploadedFile.fileType} />
          Your browser does not support the video tag.
        </video>
      );
    }

    // For documents (PDF, DOC, etc.)
    return (
      <div className="w-full h-[600px]">
        <embed
          src={uploadedFile.file_url}
          type={uploadedFile.fileType}
          width="100%"
          height="100%"
          className="rounded-md shadow-lg"
        />
      </div>
    );
  };

  return (
    <>
      <div className="flex w-full flex-col items-start rounded-md border border-solid border-neutral-border bg-default-background shadow-sm">
        <div className="flex w-full items-center gap-2 border-b border-solid border-neutral-border px-6 py-6">
          <span className="text-heading-3 font-heading-3 text-default-font">
            Upload files
          </span>
          <span className="text-body font-body text-subtext-color">
            Image, Video or Document
          </span>
        </div>

        <div className="flex w-full flex-col items-start gap-4 px-6 py-6">
          <div
            className="flex w-full flex-col items-center justify-center gap-2 rounded-md border border-dashed border-brand-600 px-6 py-6 cursor-pointer"
            onClick={() => fileInputRef.current?.click()}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
          >
            <SubframeCore.Icon
              className="text-heading-1 font-heading-1 text-brand-700"
              name="FeatherUploadCloud"
            />
            <span className="text-body font-body text-default-font text-center">
              Click to select files or drag to upload
            </span>
            <input
              type="file"
              ref={fileInputRef}
              className="hidden"
              onChange={handleFileSelect}
              accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx"
              aria-label="Upload file"
              title="Upload file"
            />
          </div>

          {uploadedFile && (
            <div className="flex w-full items-center gap-2 rounded-md bg-neutral-50 px-2 py-2">
              <div className="flex items-center gap-4 px-1 py-1">
                <SubframeCore.Icon
                  className="text-body font-body text-brand-700"
                  name="FeatherCheckCircle"
                />
              </div>
              <span className="grow shrink-0 basis-0 text-caption-bold font-caption-bold text-default-font">
                {uploadedFile.original_name}
              </span>
              <IconButton
                size="small"
                icon="FeatherTrash"
                onClick={() => setUploadedFile(null)}
              />
            </div>
          )}

          {isUploading && (
            <div className="flex w-full items-center gap-2 rounded-md bg-neutral-50 px-2 py-2">
              <div className="flex items-center gap-4 px-1 py-1">
                <SubframeCore.Icon
                  className="animate-spin text-body font-body text-brand-700"
                  name="FeatherLoader"
                />
              </div>
              <span className="grow shrink-0 basis-0 text-caption-bold font-caption-bold text-default-font">
                Uploading...
              </span>
            </div>
          )}
        </div>
      </div>

      <DialogLayout open={showPreview} onOpenChange={setShowPreview}>
        <div className="flex h-full w-full flex-col items-start gap-6 bg-default-background px-6 py-6">
          <div className="flex w-full items-center justify-between border-b border-solid border-neutral-border pb-4">
            <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1">
              <span className="text-heading-2 font-heading-2 text-default-font">
                Media Preview
              </span>
              <span className="text-body font-body text-subtext-color">
                {uploadedFile?.original_name} • {formatFileSize(uploadedFile?.size)}
              </span>
            </div>
            <IconButton
              icon="FeatherX"
              onClick={() => setShowPreview(false)}
            />
          </div>

          <div className="relative w-full grow shrink-0 basis-0 flex-col items-center justify-center gap-4 rounded-md border border-solid border-neutral-border bg-neutral-50 px-6 py-6">
            {isUploading ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
                  <div className="mt-2 text-neutral-600">Uploading...</div>
                </div>
              </div>
            ) : uploadError ? (
              <div className="text-center text-red-500 p-4">
                {uploadError}
              </div>
            ) : (
              <div className="flex flex-col items-center">
                {renderPreview()}
              </div>
            )}
          </div>

          <TextArea
            className="h-auto w-full flex-none"
            label="Caption"
            helpText=""
          >
            <TextArea.Input
              className="h-auto min-h-24 w-full flex-none"
              placeholder="Add a caption..."
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              disabled={isUploading}
            />
          </TextArea>
          <div className="flex w-full grow shrink-0 basis-0 flex-col items-center gap-4">
            <div className="flex grow shrink-0 basis-0 items-center rounded-lg bg-brand-600">
              <SubframeCore.Tooltip.Provider>
                <SubframeCore.Tooltip.Root>
                  <SubframeCore.Tooltip.Trigger asChild={true}>
                    <IconButton
                      className="h-auto w-8 flex-none self-stretch"
                      variant="brand-primary"
                      icon="FeatherCalendarClock"
                      onClick={() => setIsDateTimePopupOpen(true)}
                    />
                  </SubframeCore.Tooltip.Trigger>
                  <SubframeCore.Tooltip.Portal>
                    <SubframeCore.Tooltip.Content
                      side="bottom"
                      align="center"
                      sideOffset={4}
                      asChild={true}
                    >
                      <Tooltip>Schedule send</Tooltip>
                    </SubframeCore.Tooltip.Content>
                  </SubframeCore.Tooltip.Portal>
                </SubframeCore.Tooltip.Root>
              </SubframeCore.Tooltip.Provider>
              <div className="flex w-px flex-none flex-col items-center gap-2 self-stretch bg-brand-50" />
              <Button
                className="h-auto min-h-[36px] w-auto flex-none self-stretch"
                iconRight="FeatherSendHorizontal"
                onClick={() => handleSend()}
                disabled={isUploading || isMediaUploading || !uploadedFile || !caption.trim()}
                loading={isMediaUploading}
              >
                Send
              </Button>
            </div>
          </div>
        </div>
      </DialogLayout>

      {/* Date and Time Picker Popup */}
      <SelectDateAndTimePopup
        open={isDateTimePopupOpen}
        setSelectDateAndTimePopupOpen={setIsDateTimePopupOpen}
        onScheduleMessage={handleScheduleMessage}
        messageText={caption}  // We pass the caption as the messageText
      />
    </>
  );
};

export default FileSelectionMenu;